import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";

import { Box, Breadcrumbs, Link, Skeleton, Tab, Tabs, useMediaQuery } from "@mui/material";
import { ResponseError } from "@syadem/kairos-citizen-js";
import { useEffect, useState } from "react";
import { matchPath, Outlet, Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { calculateAge, utcToday } from "../../utils/ageCalculator";
import { localizedDate } from "../../utils/dayjs";
import { DeleteButtonIcon } from "../components/shared/DeleteButtonIcon";
import { useAuthenticatedUser } from "../hooks";
import { useHealthRecord } from "../hooks/useHealthRecord";
import { useI18n } from "../hooks/useI18n";
import { useKairosApi } from "../hooks/useKairosApi";
import { PageLayout } from "../layout/PageLayout";
import { theme } from "../layout/Theme";
import { TravelProvider } from "./health_record/travel/TravelContext";

export function HealthRecord() {
  const { id } = useParams() as { id: string };
  const { healthRecord, error } = useHealthRecord(id);
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const api = useKairosApi();
  const currentUser = useAuthenticatedUser();
  const [birthDateInput, setBirthDateInput] = useState<Date | null>(null);

  useEffect(() => {
    if (
      (error && error instanceof Response && error.status === 404) ||
      (error instanceof ResponseError && error.response.status === 404)
    ) {
      navigate("/not_found");
    }
  }, [error, navigate]);

  const breakpointSm = useMediaQuery(theme.breakpoints.down("sm"));
  const breakpointMd = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <PageLayout
      title={
        breakpointMd ? (
          <Box sx={{ ml: 1 }}>
            <Link
              underline="none"
              color={theme.palette.primary.main}
              to="/"
              component={RouterLink}
              sx={{
                "&:hover": {
                  color: theme.palette.primary[300],
                },
              }}
            >
              <Typography variant={breakpointSm ? "h6" : "h5"}>{t("health_records.title")}</Typography>
            </Link>
            <Typography
              variant="h6"
              sx={{
                fontSize: 16,
                maxWidth: "240px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {healthRecord ? (
                `${healthRecord.firstNames} ${healthRecord.lastName} ${
                  healthRecord.customaryName ? `(${healthRecord.customaryName})` : ""
                }`
              ) : (
                <Skeleton variant="text" width={150} />
              )}
            </Typography>
            <Typography variant={"subtitle2"} component="span" sx={{ color: theme.palette.neutral[500], fontSize: 14 }}>
              {healthRecord ? (
                `${localizedDate(healthRecord.birthDate, locale)} (${calculateAge(utcToday(), healthRecord.birthDate, true, t)})`
              ) : (
                <Skeleton variant="text" width={150} />
              )}
            </Typography>
            {api &&
              healthRecord &&
              (healthRecord.ownerId === currentUser?.id ? (
                <DeleteButtonIcon
                  deleteRecord={() => api.healthRecordApi.deleteHealthRecord(healthRecord.id)}
                  title={t("common.cta.delete")}
                  modalTitle={t("health_records.delete_hr")}
                  refreshCallback={() => navigate(`/health-records`)}
                  modalContent={
                    <>
                      <span style={{ color: theme.palette.error.main, fontWeight: "bold" }}>
                        {t("health_records.delete_hr_validation_intro_1")}
                      </span>
                      <span>{t("health_records.delete_hr_validation_intro_2")}</span>
                      <p>{t("health_records.delete_hr_validation")}</p>
                    </>
                  }
                  icon={<DeleteOutlineIcon fontSize="small" color="error" />}
                  variant="contained"
                  type={"icon"}
                  sx={{ p: "4px 8px" }}
                  confirmDisabled={
                    !birthDateInput || !dayjs(healthRecord.birthDate).isSame(dayjs(birthDateInput), "day")
                  }
                  birthDateInput={birthDateInput}
                  setBirthDateInput={setBirthDateInput}
                  dateFormat={t("health_records.date_format")}
                />
              ) : (
                <DeleteButtonIcon
                  deleteRecord={() => api.healthRecordApi.leaveHealthRecord(healthRecord.id)}
                  refreshCallback={() => navigate(`/health-records`)}
                  title={t("share.withdraw")}
                  modalTitle={t("share.withdraw_hr")}
                  modalContent={t("share.withdraw_hr_validation")}
                  icon={<ExitToAppIcon fontSize="small" color="error" />}
                  variant="outlined"
                  type={"icon"}
                  sx={{ p: "4px 8px" }}
                  confirmDisabled={
                    !birthDateInput || !dayjs(healthRecord.birthDate).isSame(dayjs(birthDateInput), "day")
                  }
                  birthDateInput={birthDateInput}
                  setBirthDateInput={setBirthDateInput}
                  dateFormat={t("health_records.date_format")}
                />
              ))}
          </Box>
        ) : (
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ color: theme.palette.neutral[600], fontWeight: "bold" }}
            data-testid="breadcrumbs"
            separator={<NavigateNextIcon fontSize={breakpointSm ? "small" : "medium"} />}
          >
            <Link
              underline="none"
              color={theme.palette.primary.main}
              to="/"
              component={RouterLink}
              sx={{
                "&:hover": {
                  color: theme.palette.primary[300],
                },
              }}
            >
              <Typography variant={breakpointSm ? "h6" : "h5"}>{t("health_records.title")}</Typography>
            </Link>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant={breakpointSm ? "h6" : "h5"}
                sx={{
                  maxWidth: "450px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {healthRecord ? (
                  `${healthRecord.firstNames} ${healthRecord.lastName} ${
                    healthRecord.customaryName ? `(${healthRecord.customaryName})` : ""
                  }`
                ) : (
                  <Skeleton variant="text" width={150} />
                )}
              </Typography>
              <Typography variant={"h6"} component="span" sx={{ color: theme.palette.neutral[500], ml: 1 }}>
                {healthRecord ? (
                  `(${calculateAge(utcToday(), healthRecord.birthDate, true, t)})`
                ) : (
                  <Skeleton variant="text" width={150} />
                )}
              </Typography>

              {api &&
                healthRecord &&
                (healthRecord.ownerId === currentUser?.id ? (
                  <DeleteButtonIcon
                    deleteRecord={() => api.healthRecordApi.deleteHealthRecord(healthRecord.id)}
                    title={t("common.cta.delete")}
                    modalTitle={t("health_records.delete_hr")}
                    refreshCallback={() => navigate(`/health-records`)}
                    modalContent={
                      <>
                        <span style={{ color: theme.palette.error.main, fontWeight: "bold" }}>
                          {t("health_records.delete_hr_validation_intro_1")}
                        </span>
                        <span>{t("health_records.delete_hr_validation_intro_2")}</span>
                        <p>{t("health_records.delete_hr_validation")}</p>
                      </>
                    }
                    icon={<DeleteOutlineIcon fontSize="small" color="error" />}
                    variant="contained"
                    type="icon"
                    confirmDisabled={
                      !birthDateInput || !dayjs(healthRecord.birthDate).isSame(dayjs(birthDateInput), "day")
                    }
                    birthDateInput={birthDateInput}
                    setBirthDateInput={setBirthDateInput}
                    dateFormat={t("health_records.date_format")}
                  />
                ) : (
                  <DeleteButtonIcon
                    deleteRecord={() => api.healthRecordApi.leaveHealthRecord(healthRecord.id)}
                    refreshCallback={() => navigate(`/health-records`)}
                    title={t("share.withdraw")}
                    modalTitle={t("share.withdraw_hr")}
                    modalContent={t("share.withdraw_hr_validation")}
                    icon={<ExitToAppIcon fontSize="small" color="error" />}
                    variant="outlined"
                    type={"icon"}
                    confirmDisabled={
                      !birthDateInput || !dayjs(healthRecord.birthDate).isSame(dayjs(birthDateInput), "day")
                    }
                    birthDateInput={birthDateInput}
                    setBirthDateInput={setBirthDateInput}
                    dateFormat={t("health_records.date_format")}
                  />
                ))}
            </Box>
          </Breadcrumbs>
        )
      }
      tabs={<PageTabs />}
    >
      <TravelProvider key={id} healthRecordId={id}>
        <Outlet />
      </TravelProvider>
    </PageLayout>
  );
}

function PageTabs() {
  // You need to provide the routes in descendant order.
  // This means that if you have nested routes like:
  // users, users/new, users/edit.
  // Then the order should be ['users/add', 'users/edit', 'users'].
  let currentTab = "health-records/:id/shares";
  const { t } = useI18n();
  const { pathname } = useLocation();
  const user = useAuthenticatedUser();

  if (
    matchPath("health-records/:id/vaccination/:vaccinationActId", pathname) ||
    matchPath("health-records/:id/add-vaccination", pathname) ||
    matchPath("health-records/:id", pathname)
  ) {
    currentTab = "health-records/:id";
  } else if (matchPath("health-records/:id/edit", pathname)) {
    currentTab = "health-records/:id/edit";
  } else if (matchPath("health-records/:id/profile", pathname)) {
    currentTab = "health-records/:id/profile";
  } else if (matchPath("health-records/:id/travel", pathname)) {
    currentTab = "health-records/:id/travel";
  } else if (matchPath("health-records/:id/diagnosis", pathname)) {
    currentTab = "health-records/:id/diagnosis";
  } else if (matchPath("health-records/:id/documents", pathname)) {
    currentTab = "health-records/:id/documents";
  } else if (matchPath("health-records/:id/appointments", pathname)) {
    currentTab = "health-records/:id/appointments";
  }
  const breakpointSm = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Tabs
      value={currentTab}
      textColor="primary"
      sx={{
        backgroundColor: "none",
        border: "none",
        px: { xs: 0, md: 2 },
        ".MuiButtonBase-root": {
          padding: { xs: "0 8px", md: "0 16px" },
        },
        ".MuiTabScrollButton-root": {
          color: theme.palette.primary[500],
        },
      }}
      variant={breakpointSm ? "fullWidth" : "standard"}
    >
      <Tab
        label={
          <Typography sx={{ fontSize: breakpointSm ? 10 : 14, fontWeight: 500 }}>
            {t("patients.tabs.immunization")}
          </Typography>
        }
        value="health-records/:id"
        to=""
        component={RouterLink}
      />
      <Tab
        label={
          <Typography sx={{ fontSize: breakpointSm ? 10 : 14, fontWeight: 500 }}>
            {t("patients.tabs.profile")}
          </Typography>
        }
        value="health-records/:id/profile"
        to="profile"
        component={RouterLink}
      />
      <Tab
        label={
          <Typography sx={{ fontSize: breakpointSm ? 10 : 14, fontWeight: 500 }}>
            {t("patients.tabs.travel")}
          </Typography>
        }
        value="health-records/:id/travel"
        to="travel"
        component={RouterLink}
      />
      <Tab
        label={
          <Typography sx={{ fontSize: breakpointSm ? 10 : 14, fontWeight: 500 }}>
            {t("patients.tabs.vaccinations_to_be_done")}
          </Typography>
        }
        value="health-records/:id/diagnosis"
        to="diagnosis"
        component={RouterLink}
      />
      <Tab
        label={
          <Typography sx={{ fontSize: breakpointSm ? 10 : 14, fontWeight: 500 }}>
            {t("patients.tabs.identity")}
          </Typography>
        }
        value="health-records/:id/edit"
        to="edit"
        component={RouterLink}
      />
      <Tab
        label={
          <Typography sx={{ fontSize: breakpointSm ? 10 : 14, fontWeight: 500 }}>{t("patients.tabs.share")}</Typography>
        }
        value="health-records/:id/shares"
        to="shares"
        component={RouterLink}
      />
      <Tab
        label={
          <Typography sx={{ fontSize: breakpointSm ? 10 : 14, fontWeight: 500 }}>
            {t("patients.tabs.documents")}
          </Typography>
        }
        value="health-records/:id/documents"
        to="documents"
        component={RouterLink}
      />
      {user.id === "9e255d79-9fb6-4115-b4ec-3586294a9368" ? (
        <Tab
          label={
            <Typography sx={{ fontSize: breakpointSm ? 10 : 14, fontWeight: 500 }}>
              {t("patients.tabs.appointments")}
            </Typography>
          }
          value="health-records/:id/appointments"
          to="appointments"
          component={RouterLink}
        />
      ) : null}
    </Tabs>
  );
}
